import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonsWrapper,
  ICONS,
  PLATFORM_THEME_COLORS,
  SearchInput,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';
import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { useRouteQuery } from 'src/common/helpers';
import { useGetDocumentsByIds } from 'src/hooks/documents/get-documents-by-ids.hook';
import PermissionCheck from 'src/components/permission-check';

import { CreateDocument } from './components/create-document';
import { HeaderContainer, MainWrapper, StyledExpandableTable } from './employee-documents.styles';
import { DocumentsListTable } from './components/documents-list-table';
import { EMPLOYEE_DOCUMENTS_MODALS } from './employee-documents.constants';
import { UploadDocumentModal } from './components/upload-document-modal';

const EmployeeDocuments = ({
  documentsIds,
  createdBy,
}: {
  documentsIds: string[];
  createdBy: string;
}) => {
  const { t } = useTranslation('settings');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [searchFieldValue, setSearchFieldValue] = useState('');

  const query = useRouteQuery();
  const employeeId: string = query.get('id') || '';

  const { documents, loading } = useGetDocumentsByIds(documentsIds, searchFieldValue);

  const closeModal = () => setActiveModal(null);

  return (
    <MainWrapper>
      <CreateDocument
        isOpened={activeModal === EMPLOYEE_DOCUMENTS_MODALS.createDocument}
        onClose={closeModal}
      />
      <UploadDocumentModal
        isOpened={activeModal === EMPLOYEE_DOCUMENTS_MODALS.uploadDocument}
        onClose={closeModal}
        employeeId={employeeId}
      />
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('documents.labels.documents')}
        </Typography>
        <div>
          <SearchInput onChange={e => setSearchFieldValue(e.target.value)} />
          <PermissionCheck
            permission={PLATFORM_PERMISSIONS.memberManagement}
            action={PERMISSION_ACTIONS.update}
            createdBy={createdBy}
          >
            <ButtonsWrapper>
              <Button
                type="primary"
                ghost
                icon={ICONS.upload}
                iconColor={PLATFORM_THEME_COLORS.primary.blue}
                onClick={() => setActiveModal(EMPLOYEE_DOCUMENTS_MODALS.uploadDocument)}
              >
                Upload document
              </Button>
              <Button
                type="primary"
                ghost
                icon={ICONS.plus}
                iconColor={PLATFORM_THEME_COLORS.primary.blue}
                onClick={() => setActiveModal(EMPLOYEE_DOCUMENTS_MODALS.createDocument)}
              >
                {t('documents.buttons.createNewDocument')}
              </Button>
            </ButtonsWrapper>
          </PermissionCheck>
        </div>
      </HeaderContainer>
      <StyledExpandableTable
        loading={loading}
        dataSource={documents.filter((doc: any) => doc.documentTemplates.length)}
        expandedRowRender={document => (
          <DocumentsListTable
            data={document.documentTemplates}
            documentId={document._id}
            employeeId={employeeId}
          />
        )}
      >
        <StyledExpandableTable.Column
          title={t('documentsLabels.labels')}
          sorter={(a: any, b: any) => a.documentLabel.name.localeCompare(b.documentLabel.name)}
          render={document => <Typography>{document?.documentLabel?.name}</Typography>}
        />
      </StyledExpandableTable>
    </MainWrapper>
  );
};

export default EmployeeDocuments;
